import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Beaker, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import LabsVisual from "@/components/LabsVisual";

const StatusBadge = ({ status }) => (
  <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase bg-[#00A878] text-white px-1.5 py-0.5">
    {status}
  </span>
);

const ProjectCard = ({ p, idx }) => (
  <Link
    to={`/labs/${p.slug || p.id}`}
    data-testid={`labs-card-${idx}`}
    className="group border border-[#E5E7EB] hover:border-[#111827] bg-white flex flex-col overflow-hidden transition-colors"
  >
    <LabsVisual item={p} />
    <div className="p-5 flex-1 flex flex-col">
      <div className="flex items-center gap-2">
        <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{p.project_type}</span>
        <StatusBadge status={p.project_status} />
      </div>
      <h3 className="font-display text-xl mt-2 text-[#111827] leading-snug group-hover:text-[#00A878]">
        {p.title}
      </h3>
      <p className="mt-1 text-[13px] text-[#00A878] font-mono-dl italic line-clamp-2">{p.tagline}</p>
      <p className="mt-3 text-[13px] text-[#4B5563] line-clamp-3">{p.description}</p>
      <div className="flex-1" />
      {p.tags?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {p.tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] font-mono-dl tracking-wider text-[#4B5563] border border-[#E5E7EB] px-1.5 py-0.5">
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  </Link>
);

export const Labs = () => {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/labs?language=${lang}&limit=60`).then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, [lang]);

  return (
    <div data-testid="labs-page">
      <SeoHead
        lang={lang}
        path="/labs"
        title={lang === "fr" ? "Dataleo Labs - Explore. Construis. Démontre." : "Dataleo Labs - Explore. Build. Demonstrate."}
        description={lang === "fr"
          ? "Dataleo Labs : prototypes, démonstrateurs et recherche autour des systèmes de décision Supply Chain."
          : "Dataleo Labs: prototypes, demonstrators and research on Supply Chain decision systems."}
      />
      <PageHero
        kicker={(lang === "fr" ? "Dataleo Labs · " : "Dataleo Labs · ") + (lang === "fr" ? "Terrain d'expérimentation" : "Experimentation grounds")}
        title={lang === "fr" ? "Explore. Construis. Démontre." : "Explore. Build. Demonstrate."}
        description={lang === "fr"
          ? "Prototypes, démonstrateurs, recherches et concepts autour des nouveaux systèmes de décision Supply Chain."
          : "Prototypes, demonstrators, research and concepts around new Supply Chain decision systems."}
        accent="yellow"
        testId="labs-hero"
      />
      <div className="dl-container py-14">
        {loading ? (
          <p className="text-[#9ca3af]">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-[#9ca3af]">{lang === "fr" ? "Aucun projet Labs publié." : "No Labs projects published yet."}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="labs-grid">
            {items.map((p, i) => <ProjectCard key={p.id} p={p} idx={i} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export const LabsDetail = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/labs/${slug}`).then((r) => setItem(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <Navigate to="/labs" replace />;
  if (!item) return <div className="dl-container py-20 text-[#9ca3af]">…</div>;

  return (
    <article data-testid="labs-detail" className="dl-container py-14">
      <SeoHead
        lang={lang}
        path={`/labs/${item.slug || item.id}`}
        title={`${item.title} - Dataleo Labs`}
        description={item.tagline || item.description?.slice(0, 160)}
        image={item.image_url}
      />
      <Link to="/labs" className="text-[12px] font-mono-dl uppercase text-[#4B5563] hover:text-[#00A878]">
        ← Dataleo Labs
      </Link>
      <div className="mt-6 max-w-3xl">
        <div className="flex items-center gap-2">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{item.project_type}</span>
          <StatusBadge status={item.project_status} />
          {item.category && <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">· {item.category}</span>}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#111827] leading-tight">{item.title}</h1>
        <p className="font-display text-[20px] italic text-[#00A878] mt-3">{item.tagline}</p>
      </div>
      {/* Custom project visual - beats stock photos every time */}
      <div className="mt-8 max-w-5xl">
        <LabsVisual item={item} />
      </div>
      <div className="mt-8 max-w-3xl">
        <p className="text-[16px] text-[#111827] leading-relaxed">{item.description}</p>
        {item.body_html && (
          <div className="mt-6 tutorial-body text-[15px] text-[#111827] leading-relaxed" dangerouslySetInnerHTML={{ __html: item.body_html }} />
        )}
        <div className="mt-8 flex flex-wrap gap-3">
          {item.external_demo_url && (
            <a
              href={item.external_demo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#111827] text-white px-5 py-3 text-sm font-semibold hover:bg-[#00A878] transition-colors"
            >
              {item.cta_label || (lang === "fr" ? "Ouvrir la démo" : "Open the demo")} <ExternalLink size={14} />
            </a>
          )}
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 border border-[#111827] text-[#111827] px-5 py-3 text-sm font-semibold hover:bg-[#111827] hover:text-white transition-colors"
          >
            {lang === "fr" ? "En parler avec Dataleo" : "Talk to Dataleo about this"} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
};
